import axios from 'axios';
import { supabase } from '../lib/supabaseClient';

// Get base URL from environment or use fallback URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ||
    (import.meta.env.PROD ?
        'https://geo-agent-j28t.onrender.com' :
        '');

console.log('Using API URL:', API_BASE_URL);

export const apiClient = axios.create({
    baseURL: `${API_BASE_URL}/api`,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    withCredentials: false,
    timeout: 60000 // 60 second timeout
});

// Request interceptor - add auth token if available
apiClient.interceptors.request.use(
    (config) => {
        // Use access_token instead of authToken
        const token = localStorage.getItem('access_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Log request for debugging
        console.log(`🔄 ${config.method?.toUpperCase()} ${config.url}`);

        return config;
    },
    (error) => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor - handle common errors and token refresh
apiClient.interceptors.response.use(
    (response) => {
        console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        if (error.response) {
            console.error('❌ Response error:', {
                status: error.response.status,
                data: error.response.data,
                url: error.config?.url
            });

            if (error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;

                const hadToken = !!localStorage.getItem('access_token');
                if (hadToken) {
                    try {
                        const { data, error: refreshError } = await supabase.auth.refreshSession();

                        if (data?.session && !refreshError) {
                            localStorage.setItem('access_token', data.session.access_token);
                            localStorage.setItem('refresh_token', data.session.refresh_token);
                            originalRequest.headers.Authorization = `Bearer ${data.session.access_token}`;
                            return apiClient(originalRequest);
                        }
                    } catch {}

                    sessionStorage.setItem('session_expired', 'true');
                    await supabase.auth.signOut();
                }
            }

            // Enhance error messages
            if (error.response.status >= 500) {
                error.message = 'Server error. Please try again later.';
            } else if (error.response.status === 404) {
                error.message = 'Requested resource not found.';
            } else if (error.response.status === 403) {
                error.message = 'Access forbidden. Check your permissions.';
            } else if (error.response.data?.message) {
                error.message = error.response.data.message;
            }

        } else if (error.request) {
            console.error('❌ Network error - no response received:', error.request);
            error.message = 'Network error. Please check your connection and try again.';
        } else {
            console.error('❌ Request setup error:', error.message);
        }

        return Promise.reject(error);
    }
);

// Health check function
export const checkServerHealth = async () => {
    try {
        const response = await apiClient.get('/llms/health');
        return {
            isReachable: true,
            message: 'Server is healthy',
            details: response.data
        };
    } catch (error) {
        return {
            isReachable: false,
            message: 'Server is unreachable',
            details: error instanceof Error ? error.message : String(error)
        };
    }
};
